// "Last updated" line for published union wage scales (published_wage_rates). Every public place
// that shows a local's rates uses this, so the date rules live in one spot:
//   - last updated  = newest retrieved_date among the rows shown
//   - rates from    = earliest effective_start among the rows shown (left out when none has one)
//   - expired       = every row shown has an effective_end and the latest one is before today
// Dates are plain YYYY-MM-DD; they are compared as strings and formatted in UTC so no timezone
// shifts the day.

const day = v => (v ? String(v).slice(0, 10) : null);

// Short date for the current language: "Oct 31, 2026" / "31 oct 2026".
export function fmtWageDate(iso, lang) {
  const s = day(iso);
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return s;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(String(lang || '').startsWith('es') ? 'es-US' : 'en-US', {
    timeZone: 'UTC', year: 'numeric', month: 'short', day: 'numeric',
  });
}

// Today as YYYY-MM-DD in the viewer's own timezone.
function todayIso() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
}

// { lastUpdated, effectiveFrom, expiredOn } as YYYY-MM-DD (or null) for the rows shown.
export function wageFreshness(rows, today = todayIso()) {
  const list = rows || [];
  const pick = (key, newest) => list.map(r => day(r[key])).filter(Boolean).sort()[newest ? 'pop' : 'shift']() || null;
  const lastEnd = pick('effective_end', true);
  const allEnded = list.length > 0 && list.every(r => day(r.effective_end));
  return {
    lastUpdated: pick('retrieved_date', true),
    effectiveFrom: pick('effective_start', false),
    expiredOn: allEnded && lastEnd < today ? lastEnd : null,
  };
}

// The text to show for one local's rows: { expired, text } or null when there is no date at all.
// local: name it in the expired notice (for places that show several locals in one figure).
export function wageFreshnessText(rows, t, lang, { local } = {}) {
  const f = wageFreshness(rows);
  if (f.expiredOn) {
    const date = fmtWageDate(f.expiredOn, lang);
    return { expired: true, text: local ? t('unionWages.freshness.expiredLocal', { local, date }) : t('unionWages.freshness.expired', { date }) };
  }
  const parts = [];
  if (f.lastUpdated) parts.push(t('unionWages.freshness.lastUpdated', { date: fmtWageDate(f.lastUpdated, lang) }));
  if (f.effectiveFrom) parts.push(t('unionWages.freshness.ratesEffective', { date: fmtWageDate(f.effectiveFrom, lang) }));
  return parts.length ? { expired: false, text: parts.join(' · ') } : null;
}
